import { useAuth0 } from '@auth0/auth0-react';
import * as LucideIcons from 'lucide-react';
import { marked } from 'marked';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FeedbackAndCopy from '../askquestion/FeedbackAndCopy';
import Modal from '../common/Modal';
import ModalForm from '../common/ModalForm';
import { showToast } from '../common/ToastHandler';
import AnimatedButton from '../custom/AnimatedButton';
import EptDataTable from '../custom/EptDataTable';
import FileUpload from './blocks/FileUpload';
import MetricBlock from './blocks/MetricBlock';
import './ButtonList.css';
import { useDataReload } from './DataReloadContext';
import { useFeedbackHandler } from './utils/FeedbackHandler';

interface ButtonAction {
  type: 'api_call' | 'navigate';
  method?: string;
  url?: string;
  headers?: string;
  body?: string;
  response?: {
    status_handlers?: Array<{
      status_code: number;
      show_toast?: boolean;
      toast_type?: string;
      message?: string;
      redirect_url?: string;
    }>;
    default_handler?: {
      show_toast?: boolean;
      toast_type?: string;
      message?: string;
      redirect_url?: string;
    };
    refresh_page?: boolean;
    reload_data?: boolean;
  };
}

interface Button {
  label: string;
  variant?: string;
  icon?: {
    value: string;
  };
  action?: ButtonAction;
  modal_display?: any;
}

interface ButtonListBlock {
  buttons: Button[];
  layout?: string;
  response_id?: string;
}

interface CardContext {
  card_id?: string;
  title?: string;
}

interface ButtonListProps {
  block: ButtonListBlock;
  cardContext?: CardContext;
}

const ButtonList: React.FC<ButtonListProps> = ({ block, cardContext }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [feedbackState, setFeedbackState] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { getIdTokenClaims } = useAuth0();
  const { reloadData } = useDataReload();
  const { sendFeedback } = useFeedbackHandler();

  // Initialize and reset feedback state when block changes
  useEffect(() => {
    // Extract feedback state from button icons and initialize feedbackState
    const buttonFeedback = extractFeedbackFromButtons();
    setFeedbackState(buttonFeedback);
  }, [block]);

  const parseApiUrl = (url: string): string => {
    // Check if URL starts with https://api.dev-ept-ai.com/
    if (url.startsWith('https://api.dev-ept-ai.com/')) {
      try {
        const urlObj = new URL(url);
        const searchParams = urlObj.search;

        // Return current pathname with extracted query parameters
        return searchParams
          ? `${location.pathname}${searchParams}`
          : location.pathname;
      } catch (error) {
        console.error('Error parsing URL:', error);
        return url; // Return original URL if parsing fails
      }
    }
    return url; // Return original URL if it doesn't match the pattern
  };

  const executeApiCall = async (action: ButtonAction) => {
    setIsLoading(true);

    try {
      // Get auth token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      if (!idToken) {
        throw new Error('Authentication token not available');
      }

      // Parse headers
      let headers = {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };

      if (action.headers) {
        try {
          const customHeaders = JSON.parse(action.headers);
          headers = { ...headers, ...customHeaders };
        } catch (error) {
          console.error('Failed to parse custom headers:', error);
        }
      }

      // Parse body
      let body = null;
      if (action.body && action.method !== 'GET') {
        try {
          // Validate that body is valid JSON
          JSON.parse(action.body);
          body = action.body;
        } catch (error) {
          console.error('Failed to parse request body:', error);
          throw new Error('Invalid request body format');
        }
      }

      // Make API call
      const response = await fetch(action.url, {
        method: action.method,
        headers,
        body,
      });

      // Find appropriate status handler
      const statusHandler =
        action.response?.status_handlers?.find(
          handler => handler.status_code === response.status
        ) || action.response?.default_handler;

      // Show toast if configured
      if (statusHandler?.show_toast) {
        showToast(statusHandler.toast_type || 'success', statusHandler.message);
      }

      // If we're on Response Details and this was a like/dislike or feedback action, refresh the page
      const isResponseDetailsPage = location.pathname.startsWith('/response/');
      const isRankingEndpoint = (action.url || '').includes(
        '/response/ranking'
      );
      if (isResponseDetailsPage && isRankingEndpoint) {
        // Always refresh on success
        window.location.reload();
      }

      // Handle redirect
      if (statusHandler?.redirect_url) {
        const parsedRedirectUrl = parseApiUrl(statusHandler.redirect_url);
        if (parsedRedirectUrl.startsWith('/')) {
          navigate(parsedRedirectUrl);
        } else {
          window.open(parsedRedirectUrl, '_blank');
        }
      }

      // Handle page refresh/reload
      if (action.response?.refresh_page) {
        window.location.reload();
      } else if (action.response?.reload_data && reloadData) {
        reloadData();
      }
    } catch (error) {
      console.error('API call failed:', error);

      // Use default handler for errors
      const defaultHandler = action.response?.default_handler;
      if (defaultHandler?.show_toast) {
        showToast(
          defaultHandler.toast_type || 'error',
          defaultHandler.message || 'An unexpected error occurred.'
        );
      } else {
        showToast(
          'error',
          error.message || 'Request failed. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (button: Button) => {
    // Handle Response ID copy on conversation detail pages
    if (isConversationDetails) {
      const responseId = extractResponseIdFromLabel(button.label);
      if (responseId) {
        copyResponseId(responseId);
        return;
      }
    }

    // Handle API call action
    if (button.action && button.action.type === 'api_call') {
      // Still execute the API in the background
      executeApiCall(button.action);
      return;
    }

    // Handle navigation action
    if (button.action && button.action.type === 'navigate') {
      const url = button.action.url;
      const parsedUrl = parseApiUrl(url);

      // Check if it's a local route or external URL
      if (parsedUrl.startsWith('/')) {
        navigate(parsedUrl);
      } else {
        window.open(parsedUrl, '_blank');
      }
      return;
    }

    // Handle modal display
    if (button.modal_display) {
      setModalContent(button.modal_display);
      setIsModalOpen(true);
    }
  };

  const toPascal = (name: string): string =>
    (name || '')
      .split(/[-_\s]/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join('');
  const IconComponent = (iconName: string, props: any = {}) => {
    const Icon = LucideIcons[toPascal(iconName)];
    return Icon ? <Icon {...props} /> : null;
  };

  // Helper function to convert markdown links to HTML
  const convertMarkdownLinks = (text: string): string => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return text.replace(
      linkRegex,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
  };

  // Helper function to convert \n to <br> tags
  const convertNewlines = (text: string): string => {
    return text.replace(/\n/g, '<br />');
  };

  const renderModalContent = () => {
    if (!modalContent) return null;

    // Handle array of content blocks
    if (Array.isArray(modalContent.content)) {
      return (
        <div className="modal-content-blocks">
          {modalContent.content.map((contentBlock, index) => (
            <div key={index} className="modal-content-block">
              {renderSingleContentBlock(contentBlock)}
            </div>
          ))}
        </div>
      );
    }

    // Handle single content block
    return renderSingleContentBlock(modalContent.content);
  };

  const renderSingleContentBlock = (contentBlock: any) => {
    if (!contentBlock) return null;

    switch (contentBlock.kind) {
      case 'drag_drop_upload':
        return (
          <FileUpload
            config={contentBlock.config}
            onUploadComplete={files => {
              if (files.length > 0) {
                setIsModalOpen(false);
                setModalContent(null);
              }
            }}
          />
        );
      case 'form':
        return (
          <ModalForm
            fields={contentBlock.fields}
            submitAction={contentBlock.submit_action}
            rowData={{}}
            onSubmit={formData => {
              if (contentBlock.submit_action.response.clear_form) {
                setIsModalOpen(false);
                setModalContent(null);
              }
            }}
          />
        );
      case 'text':
        return (
          <div
            className="modal-text-content"
            dangerouslySetInnerHTML={{
              __html:
                contentBlock.content !== ''
                  ? String(marked.parse(contentBlock.content))
                  : '',
            }}
            style={{
              lineHeight: '1.6',
              fontSize: '14px',
              color: '#333',
            }}
          />
        );
      case 'metric':
        return <MetricBlock block={contentBlock} />;
      case 'simple_table':
        // Transform simple_table config to EptDataTable format
        const tableConfig = {
          data_api: contentBlock.data_api,
          columns: contentBlock.columns.map(col => ({
            ...col,
            type_: col.type || 'text',
          })),
          table_features: {
            pagination: {
              enabled: contentBlock.pagination !== false,
              default_limit: 10,
            },
            search: {
              enabled: contentBlock.search === true,
            },
          },
          search: contentBlock.search
            ? {
                enabled: true,
                query_param: 'search',
                placeholder: 'Search...',
              }
            : { enabled: false },
          default_query_params: {
            page: 1,
            limit: 10,
          },
          row_actions: contentBlock.row_actions,
        };
        return <EptDataTable block={tableConfig} />;
      default:
        return null;
    }
  };

  const isResponseDetails = location.pathname.startsWith('/response/');
  const isConversationDetails = location.pathname.startsWith('/conversation/');
  const isKsDetails =
    location.pathname.startsWith('/knowledge-sources/') ||
    location.pathname.startsWith('/settings/ks-details/');

  // Extract response_id from button actions
  const extractResponseId = (): string | undefined => {
    const buttons = block.buttons || [];
    for (const button of buttons) {
      if (button.action && button.action.body) {
        try {
          const bodyData = JSON.parse(button.action.body);
          if (bodyData.response_id) {
            return bodyData.response_id;
          }
        } catch (e) {
          // Continue if JSON parsing fails
        }
      }
    }
    return block.response_id; // Fallback to direct property
  };

  // Extract response ID from button label (for conversation detail pages)
  const extractResponseIdFromLabel = (label: string): string | null => {
    const match = label.match(/Response ID:\s*([a-f0-9-]+)/i);
    return match ? match[1] : null;
  };

  // Copy response ID to clipboard
  const copyResponseId = async (responseId: string) => {
    try {
      await navigator.clipboard.writeText(responseId);
      showToast('success', 'Response ID copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = responseId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast('success', 'Response ID copied to clipboard!');
    }
  };

  // Extract feedback state from button icon values
  const extractFeedbackFromButtons = () => {
    const buttons = block.buttons || [];
    let isLiked = false;
    let isDisliked = false;

    buttons.forEach(button => {
      if (button.label === 'Like' && button.icon?.value === 'thumbs-up-true') {
        isLiked = true;
      }
      if (
        button.label === 'Dislike' &&
        button.icon?.value === 'thumbs-down-true'
      ) {
        isDisliked = true;
      }
    });

    if (isLiked) {
      return { user_feedback: 'like', likes: 1, dislikes: 0 };
    }
    if (isDisliked) {
      return { user_feedback: 'dislike', likes: 0, dislikes: 1 };
    }
    return { user_feedback: null, likes: 0, dislikes: 0 };
  };

  // Transform feedback data to match FeedbackAndCopy component expectations
  const transformFeedbackData = (feedbackData: any) => {
    if (!feedbackData) return null;

    return {
      ratings: {
        thumbs_up: feedbackData.user_feedback === 'like' ? 1 : 0,
        thumbs_down: feedbackData.user_feedback === 'dislike' ? 1 : 0,
        text: '',
      },
    };
  };

  const responseId = extractResponseId();

  // Handle feedback submission with state update
  const handleSendFeedback = async (
    response_id,
    feedback,
    itemurl,
    resolve,
    currentRanking
  ) => {
    try {
      const currentFeedbackData = feedbackState || extractFeedbackFromButtons();

      // Check if user is clicking the same button (toggle functionality)
      const isCurrentlyLiked = currentFeedbackData?.user_feedback === 'like';
      const isCurrentlyDisliked =
        currentFeedbackData?.user_feedback === 'dislike';

      let actualFeedback = feedback;
      let shouldNeutralize = false;

      // If clicking the same button, neutralize the feedback
      if (
        (feedback === true && isCurrentlyLiked) ||
        (feedback === false && isCurrentlyDisliked)
      ) {
        shouldNeutralize = true;
        actualFeedback = null; // Neutral state
      }

      const result = await sendFeedback(
        response_id,
        actualFeedback,
        itemurl,
        resolve,
        currentRanking
      );

      // Update local feedback state based on the response
      if (result && !result.error) {
        let newFeedbackState;

        if (shouldNeutralize) {
          // Remove feedback (neutral state)
          newFeedbackState = {
            user_feedback: null,
            likes: 0,
            dislikes: 0,
          };
        } else {
          // Set new feedback
          newFeedbackState = {
            user_feedback: feedback ? 'like' : 'dislike',
            likes: feedback ? 1 : 0,
            dislikes: feedback ? 0 : 1,
          };
        }

        setFeedbackState(newFeedbackState);
      }

      return result;
    } catch (error) {
      console.error('Error in handleSendFeedback:', error);
      return { error };
    }
  };

  // Use feedbackState if available, otherwise extract from button icons
  const currentFeedbackData = feedbackState || extractFeedbackFromButtons();
  const currentRanking = transformFeedbackData(currentFeedbackData);

  if (isResponseDetails) {
    const iconLabels = ['Like', 'Dislike', 'Copy'];
    const iconButtons = (block.buttons || []).filter(b =>
      iconLabels.includes(b.label)
    );
    const otherButtons = (block.buttons || []).filter(
      b => !iconLabels.includes(b.label)
    );

    // Check if this block contains standard feedback buttons (Like, Dislike, Copy)
    // If it has these feedback buttons, show only FeedbackAndCopy component
    const hasFeedbackButtons = (block.buttons || []).some(button => {
      const isLike =
        button.label === 'Like' &&
        button.action?.url?.includes('/response/ranking');
      const isDislike =
        button.label === 'Dislike' &&
        button.action?.url?.includes('/response/ranking');
      const isCopy =
        button.label === 'Copy' &&
        (button.action?.url?.includes('/response_copy') ||
          button.action?.url?.includes('/response/ranking'));
      return isLike || isDislike || isCopy;
    });

    return (
      <>
        <div
          className={`button-list ${block.layout || ''}`}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {hasFeedbackButtons && (
            <FeedbackAndCopy
              response_id={responseId}
              sendFeedback={handleSendFeedback}
              currentRanking={currentRanking}
              copyId={'response-answer-content'}
            />
          )}

          <div
            className="button-right-group"
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            {otherButtons.map((button, index) => (
              <AnimatedButton
                key={`btn-${index}`}
                className={`btn btn-${button.variant} ${isLoading ? 'disabled' : ''}`}
                onClick={() => handleButtonClick(button)}
                disabled={isLoading}
              >
                {button.icon && (
                  <span className="button-icon">
                    {IconComponent(button.icon.value)}
                  </span>
                )}
                {isLoading && button.action?.type === 'api_call'
                  ? 'Processing...'
                  : button.label}
              </AnimatedButton>
            ))}
          </div>
        </div>

        {/* Modal */}
        {modalContent && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
            title={modalContent.modal_title}
            content={renderModalContent()}
            size={modalContent.size}
            hasCloseButton={modalContent.has_close_button}
          />
        )}
      </>
    );
  }

  // KS Details page customization: right-align primary action, compact upload on left
  if (isKsDetails) {
    const buttons = block.buttons || [];
    const primaryButtons = buttons.filter(
      b =>
        b.label === 'New Guided Knowledge Document' ||
        b.label === 'New Canned responses'
    );
    const uploadButton = buttons.find(b =>
      (b.label || '').toLowerCase().includes('upload')
    );

    return (
      <>
        <div
          className={`button-list ${block.layout || ''}`}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {uploadButton && (
              <AnimatedButton
                key={`ks-upload`}
                className={`btn btn-variant-primary ${isLoading ? 'disabled' : ''}`}
                onClick={() => handleButtonClick(uploadButton)}
                disabled={isLoading}
              >
                {uploadButton.icon && (
                  <span
                    className="button-icon"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {IconComponent(uploadButton.icon.value)}
                  </span>
                )}
                {isLoading && uploadButton.action?.type === 'api_call'
                  ? 'Processing...'
                  : 'Upload Document'}
              </AnimatedButton>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {primaryButtons.map((button, index) => (
              <AnimatedButton
                key={`ksbtn-${index}`}
                className={`btn btn-${button.variant} ${isLoading ? 'disabled' : ''}`}
                onClick={() => handleButtonClick(button)}
                disabled={isLoading}
              >
                {button.icon && (
                  <span
                    className="button-icon"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {IconComponent(button.icon.value)}
                  </span>
                )}
                {isLoading && button.action?.type === 'api_call'
                  ? 'Processing...'
                  : button.label}
              </AnimatedButton>
            ))}
          </div>
        </div>

        {/* Modal */}
        {modalContent && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
            title={modalContent.modal_title}
            content={renderModalContent()}
            size={modalContent.size}
            hasCloseButton={modalContent.has_close_button}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={`button-list ${block.layout || ''}`}>
        {block.buttons.map((button, index) => {
          const responseId = isConversationDetails
            ? extractResponseIdFromLabel(button.label)
            : null;
          const isResponseIdButton = responseId !== null;

          return (
            <AnimatedButton
              key={index}
              className={`btn btn-${button.variant} ${isLoading ? 'disabled' : ''} ${isResponseIdButton ? 'response-id-button' : ''}`}
              onClick={() => handleButtonClick(button)}
              disabled={isLoading}
              style={
                isResponseIdButton
                  ? {
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }
                  : undefined
              }
            >
              {button.icon && (
                <span className="button-icon">
                  {IconComponent(button.icon.value)}
                </span>
              )}
              {isLoading && button.action?.type === 'api_call'
                ? 'Processing...'
                : button.label}
              {isResponseIdButton && (
                <span
                  className="copy-icon"
                  style={{ marginLeft: '8px', cursor: 'pointer' }}
                >
                  {IconComponent('Copy', { size: 16 })}
                </span>
              )}
            </AnimatedButton>
          );
        })}
      </div>

      {/* Modal */}
      {modalContent && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setModalContent(null);
          }}
          title={modalContent.modal_title}
          content={renderModalContent()}
          size={modalContent.size}
          hasCloseButton={modalContent.has_close_button}
        />
      )}
    </>
  );
};

export default ButtonList;
